/**
 * Step 4: Review & Create Strategy
 *
 * - Strategy naming
 * - Full configuration summary
 * - Create button
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import {
  TrendingUp as IndicatorIcon,
  Login as EntryIcon,
  Logout as ExitIcon,
  AccountBalance as LotSizerIcon,
  Shield as RiskIcon,
  GridOn as GridIcon
} from '@mui/icons-material';

const ReviewStep = ({ formData, updateFormData, errors }) => {
  // Section card styles
  const cardSx = {
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    mb: 2
  };

  // Render parameter summary
  const renderParams = (values, parameters) => {
    if (!values || !parameters || parameters.length === 0) return null;

    const entries = parameters.map(param => ({
      label: param.label,
      value: values[param.name] ?? param.defaultValue ?? '-'
    })).filter(e => e.value !== undefined && e.value !== null && e.value !== '');

    if (entries.length === 0) return null;

    return (
      <Box sx={{ mt: 1, pl: 2 }}>
        {entries.map((entry, idx) => (
          <Typography
            key={idx}
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8125rem' }}
          >
            {entry.label}: <span style={{ color: '#fff' }}>{String(entry.value)}</span>
          </Typography>
        ))}
      </Box>
    );
  };

  // Render component summary card
  const renderComponentCard = (icon, title, color, component, isOptional = false) => {
    if (!component && isOptional) return null;

    return (
      <Card sx={cardSx}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontWeight: 600, color, flex: 1 }}>{title}</Typography>
            {!component && (
              <Chip
                label="Not Selected"
                size="small"
                sx={{
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Box>
          {component && (
            <>
              <Typography sx={{ color: '#fff', mt: 1, fontWeight: 500 }}>
                {component.component?.name || component.componentId}
              </Typography>
              {renderParams(component.values, component.parameters)}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
        Review Strategy
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.6)' }}>
        Review your configuration and give your strategy a name.
      </Typography>

      {/* Strategy Name */}
      <Card sx={{ ...cardSx, border: errors.name ? '1px solid #ef4444' : cardSx.border }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2563eb', mb: 2 }}>
            Strategy Name <span style={{ color: '#ef4444' }}>*</span>
          </Typography>

          {errors.name && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              {errors.name}
            </Alert>
          )}

          <TextField
            fullWidth
            placeholder="Enter strategy name (e.g., GoldArb RSI Strategy)"
            value={formData.name || ''}
            onChange={(e) => updateFormData({ name: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#2563eb' }
              },
              '& input::placeholder': { color: 'rgba(255,255,255,0.4)' }
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Description (optional)"
            value={formData.description || ''}
            onChange={(e) => updateFormData({ description: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#2563eb' }
              },
              '& textarea::placeholder': { color: 'rgba(255,255,255,0.4)' }
            }}
          />
        </CardContent>
      </Card>

      <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
        Configuration Summary
      </Typography>

      {/* Indicators */}
      <Card sx={cardSx}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#8b5cf6', display: 'flex' }}>
              <IndicatorIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: '#8b5cf6', flex: 1 }}>
              Indicators
            </Typography>
            <Chip
              label={formData.indicators?.length || 0}
              size="small"
              sx={{
                backgroundColor: 'rgba(139, 92, 246, 0.15)',
                color: '#8b5cf6',
                fontWeight: 600
              }}
            />
          </Box>
          {formData.indicators && formData.indicators.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {formData.indicators.map((ind, idx) => (
                <Box key={idx} sx={{ mb: 1, pl: 1, borderLeft: '2px solid rgba(139, 92, 246, 0.3)' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.875rem' }}>
                    {ind.component?.name || ind.componentId}
                  </Typography>
                  {renderParams(ind.values, ind.parameters)}
                </Box>
              ))}
            </Box>
          )}
          {(!formData.indicators || formData.indicators.length === 0) && (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, fontSize: '0.875rem' }}>
              No indicators selected
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Entry & Exit Rules */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderComponentCard(
            <EntryIcon sx={{ fontSize: 20 }} />,
            'Entry Rule',
            '#22c55e',
            formData.entryRule
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderComponentCard(
            <ExitIcon sx={{ fontSize: 20 }} />,
            'Exit Rule',
            '#ef4444',
            formData.exitRule
          )}
        </Grid>
      </Grid>

      {/* Lot Sizer & Risk Manager */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderComponentCard(
            <LotSizerIcon sx={{ fontSize: 20 }} />,
            'Lot Sizer',
            '#2563eb',
            formData.lotSizer
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderComponentCard(
            <RiskIcon sx={{ fontSize: 20 }} />,
            'Risk Manager',
            '#f59e0b',
            formData.riskManager,
            true
          )}
        </Grid>
      </Grid>

      {/* Grid Trading */}
      {formData.enableGrid && (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: '#8b5cf6', display: 'flex' }}>
                <GridIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                Grid Trading
              </Typography>
              <Chip
                label="Enabled"
                size="small"
                sx={{
                  backgroundColor: 'rgba(139, 92, 246, 0.15)',
                  color: '#8b5cf6',
                  fontSize: '0.75rem'
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            '& .MuiAlert-icon': { color: '#f59e0b' }
          }}
        >
          Please fix the errors above before creating the strategy.
        </Alert>
      )}
    </Box>
  );
};

export default ReviewStep;
